(() => {
  const SELECTOR = '[data-orven-slider]';

  function initSlider(slider) {
    if (!slider || slider.dataset.orvenV56Ready === '1') return;
    slider.dataset.orvenV56Ready = '1';

    const shell = slider.closest('.orven-slider-shell');
    const prev = shell ? shell.querySelector('.orven-slider-arrow--prev') : null;
    const next = shell ? shell.querySelector('.orven-slider-arrow--next') : null;

    const autoplay = slider.dataset.autoplay !== 'false';
    const delay = 3000;

    let timer = null;
    let userInteracting = false;
    let originalCount = 0;
    let originalWidth = 0;
    let normalizeTimer = null;

    const originalCards = () =>
      Array.from(slider.querySelectorAll('.home-collection-card:not([data-orven-clone])'));

    const allCards = () =>
      Array.from(slider.querySelectorAll('.home-collection-card'));

    function cardStep() {
      const first = allCards()[0];
      if (!first) return slider.clientWidth;
      const styles = getComputedStyle(slider);
      const gap = parseFloat(styles.gap || styles.columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function recalc() {
      originalCount = originalCards().length;
      originalWidth = cardStep() * originalCount;
    }

    function buildInfiniteTrack() {
      const originals = originalCards();
      originalCount = originals.length;

      if (originalCount < 2 || slider.dataset.orvenInfiniteBuilt === '1') {
        recalc();
        return;
      }

      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      originals.forEach(card => {
        const clone = card.cloneNode(true);
        clone.dataset.orvenClone = 'before';
        before.appendChild(clone);
      });

      originals.forEach(card => {
        const clone = card.cloneNode(true);
        clone.dataset.orvenClone = 'after';
        after.appendChild(clone);
      });

      slider.insertBefore(before, slider.firstChild);
      slider.appendChild(after);
      slider.dataset.orvenInfiniteBuilt = '1';

      requestAnimationFrame(() => {
        recalc();
        slider.scrollTo({ left: originalWidth, behavior: 'auto' });
      });
    }

    function normalizePosition() {
      if (!originalWidth) recalc();
      if (!originalWidth) return;

      // Same visual card exists in all three sets.
      // Reposition instantly only after smooth movement is finished,
      // so the customer never sees a jump.
      if (slider.scrollLeft >= originalWidth * 2 - 4) {
        slider.scrollTo({
          left: slider.scrollLeft - originalWidth,
          behavior: 'auto'
        });
      } else if (slider.scrollLeft <= 4) {
        slider.scrollTo({
          left: slider.scrollLeft + originalWidth,
          behavior: 'auto'
        });
      }
    }

    function smoothBy(amount) {
      if (originalCount < 2) return;

      slider.scrollBy({
        left: amount,
        behavior: 'smooth'
      });

      clearTimeout(normalizeTimer);
      normalizeTimer = setTimeout(normalizePosition, 850);
    }

    function goNext() {
      smoothBy(cardStep());
    }

    function goPrev() {
      smoothBy(-cardStep());
    }

    function stopAuto() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function startAuto() {
      stopAuto();

      if (!autoplay || userInteracting || document.hidden || originalCount < 2) return;

      timer = setInterval(() => {
        if (!userInteracting && !document.hidden) {
          goNext();
        }
      }, delay);
    }

    buildInfiniteTrack();

    // Arrow controls.
    if (prev) {
      prev.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        goPrev();
        startAuto();
      });
    }

    if (next) {
      next.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        goNext();
        startAuto();
      });
    }

    // MOBILE: keep true native finger swipe.
    slider.addEventListener('touchstart', () => {
      userInteracting = true;
      stopAuto();
    }, { passive: true });

    slider.addEventListener('touchend', () => {
      setTimeout(() => {
        normalizePosition();
        userInteracting = false;
        startAuto();
      }, 900);
    }, { passive: true });

    slider.addEventListener('touchcancel', () => {
      userInteracting = false;
      startAuto();
    }, { passive: true });

    // Wheel/trackpad horizontal interaction on desktop is still supported.
    slider.addEventListener('wheel', () => {
      userInteracting = true;
      stopAuto();

      clearTimeout(slider._orvenWheelTimer);
      slider._orvenWheelTimer = setTimeout(() => {
        normalizePosition();
        userInteracting = false;
        startAuto();
      }, 1200);
    }, { passive: true });

    // IMPORTANT:
    // We do not prevent pointer/click events on .home-collection-card.
    // They remain normal <a href="..."> links, including cloned cards.
    slider.querySelectorAll('a.home-collection-card').forEach(card => {
      card.style.pointerEvents = 'auto';
    });

    // Extra safety: a normal primary click always follows the card URL.
    // Modifier-clicks (Ctrl/Cmd/Shift) keep their native browser behavior.
    slider.addEventListener('click', (event) => {
      const card = event.target.closest('a.home-collection-card');
      if (!card) return;

      if (
        event.button === 0 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        !event.altKey
      ) {
        // Leave the anchor default action intact. If another theme script
        // has cancelled it, force the intended navigation one tick later.
        const href = card.getAttribute('href');
        if (href) {
          setTimeout(() => {
            if (window.location.href === document.location.href) {
              window.location.assign(href);
            }
          }, 0);
        }
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAuto();
      else startAuto();
    });

    // Recalculate when responsive card widths change.
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        // Preserve which visual card we are around while resizing.
        const oldWidth = originalWidth || 1;
        const relativePosition = (slider.scrollLeft - oldWidth) / oldWidth;

        recalc();

        slider.scrollTo({
          left: originalWidth + relativePosition * originalWidth,
          behavior: 'auto'
        });

        normalizePosition();
      }, 180);
    });

    // Start autoplay after initial layout settles.
    setTimeout(startAuto, 700);
  }

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();